import { CHEATS_GUI_ENABLED_STORAGE_KEY } from './CheatsToggle';
import { cheats } from '../../Cheats';
import Game from '../../Game';
import { getHighestLevelCompleted } from '../../State/Player/PlayerLevelHelper';
import { BoosterType, PreBoosterType } from '../../State/Card/BoosterType';
import { RewardType } from '../Rewards/RewardType';
import { MobileConsole } from './MobileConsole';
import { Logger } from '../Logger/Logger';
import { LogOrigin } from '../Logger/LogOrigin';
import { PlayerTask } from '../../State/Player/PlayerTasks';
import { TimerType } from '../../System/Timer/TimerTypes';
import { PopupID } from '../../State/UI/PopupID';
import DateHelper, { formatCheatDate, getCheatsDate, parseCheatDate, setServerDate } from '../DateHelper';
import { PetId } from '../../Constants/Pets';
import { AdDebugMode } from 'State/Game/AdDebugMode';
import initDebugMenu, { DebugMenu, DebugMenuOptions, PropType } from './InitDebugMenu';
import { GameActions } from 'State/Game/GameActions';
import { IGameState } from 'State/Game/IGameState';
import { LanguageKey } from 'Utility/SDK/Language';
import { getSimulatorPersonas, personaEnabled, getSimulationAttempts } from 'Utility/Simulator';

export class CheatsGui {
    private visible: boolean = false;
    private mobileConsole: MobileConsole;
    private debugMenu: DebugMenu | null = null;

    private static firstInstance: CheatsGui | null = null;

    public static getFirstInstance(): CheatsGui | null {
        return this.firstInstance;
    }

    constructor() {
        if (CheatsGui.firstInstance === null) {
            CheatsGui.firstInstance = this;
        }

        this.mobileConsole = new MobileConsole();

        this.enableDebugMenu();
    }

    public async enableDebugMenu() {
        const props = this.getDebugProps(Game.store.getState());
        const createDebugMenu = await initDebugMenu();
        this.debugMenu = createDebugMenu({ showOnStart: true, width: 350, props });
    }

    public toggle(): void {
        this.visible = !this.visible;
        this.visible ? this.debugMenu?.gui.show() : this.debugMenu?.gui.hide();

        if (this.visible) {
            window.localStorage?.setItem(CHEATS_GUI_ENABLED_STORAGE_KEY, '1');
        } else {
            window.localStorage?.removeItem(CHEATS_GUI_ENABLED_STORAGE_KEY);
        }
    }

    private getDebugProps(state: IGameState): DebugMenuOptions['props'] {
        let selectedMission: number = 0;
        return [
            {
                type: PropType.BUTTON,
                propName: 'Complete Level',
                onPress: () => {
                    cheats.completeLevel();
                },
            },
            {
                type: PropType.NUMBER_WITH_BUTTON,
                propName: 'Level',
                initialValue: getHighestLevelCompleted(Game.store.getState().playerState),
                buttonLabel: 'Set',
                onButtonClick: value => {
                    cheats.setLevel(value);
                },
                onFinishChange: _ => { },
            },
            {
                type: PropType.TOGGLE,
                propName: 'toggle automatch',
                initialValue: false,
                onChange: value => {
                    cheats.toggleAutoMatch(value);
                },
            },
            {
                type: PropType.BUTTON,
                propName: 'Unlock Charly',
                onPress: () => {
                    cheats.setActivePet(PetId.Charly, true);
                },
            },
            {
                folderName: 'time/dates',
                props: [
                    {
                        type: PropType.LABEL,
                        propName: 'serverDate',
                        initialValue: DateHelper.now.toString(),
                        onStateChanged: _ => DateHelper.now.toString(),
                    },
                    {
                        type: PropType.STRING_WITH_BUTTON,
                        propName: 'ServerDate',
                        initialValue: getCheatsDate(),
                        buttonLabel: 'Set',
                        onButtonClick: (value): void => {
                            const numbers = value.split('.');
                            cheats.setServerDate(
                                numbers[0] as any,
                                numbers[1] as any,
                                numbers[2] as any,
                                numbers[3] as any,
                                numbers[4] as any,
                                numbers[5] as any
                            );
                        },
                        onFinishChange: (_) => { },
                    },
                    {
                        type: PropType.STRING_WITH_BUTTON,
                        propName: 'XSession',
                        initialValue: cheats.getXSessionDate() ? formatCheatDate(cheats.getXSessionDate()) : '',
                        buttonLabel: 'Set',
                        onButtonClick: (value): void => {
                            if (!value.trim()) {
                                cheats.setXSessionDate('');
                            } else {
                                const date = parseCheatDate(value);
                                cheats.setXSessionDate(date.toISOString());
                            }
                        },
                        onFinishChange: (_) => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'add Days',
                        initialValue: 0,
                        buttonLabel: 'Add',
                        onButtonClick: value => {
                            cheats.addDays(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'add Hours',
                        initialValue: 0,
                        buttonLabel: 'Add',
                        onButtonClick: value => {
                            cheats.addHours(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'add Minutes',
                        initialValue: 0,
                        buttonLabel: 'Add',
                        onButtonClick: value => {
                            cheats.addMinutes(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'set date to now',
                        onPress: () => {
                            setServerDate(Date.now());
                            GameActions.loadWorld();
                        },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Server time offset',
                        initialValue: DateHelper.now.getTime() - Date.now(),
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setServerTime(value);
                        },
                        onFinishChange: value => { },
                    },
                ],
            },
            {
                folderName: 'Resets',
                props: [
                    {
                        type: PropType.BUTTON,
                        propName: 'Clear player data',
                        onPress: () => {
                            cheats.clearPlayerData();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Clear pre-boosters',
                        onPress: () => {
                            cheats.clearPreBoosters();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset friend data',
                        onPress: () => {
                            cheats.resetFriendData();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset FTUX',
                        onPress: () => {
                            cheats.resetFTUX();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset FB event',
                        onPress: () => {
                            cheats.resetFBEvent();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset fields',
                        onPress: () => {
                            cheats.resetFields();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset timers',
                        onPress: () => {
                            cheats.resetTimers();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset Flags',
                        onPress: () => {
                            cheats.resetFlags();
                        },
                    },
                ],
            },
            {
                folderName: 'setResources',
                props: [
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Coins',
                        initialValue: state.playerState.inventory.coins,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setCoins(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'World',
                        initialValue: state.playerState.maxWorld,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setWorld(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Ads Watched',
                        initialValue: state.playerState.progress.adsWatched,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setAdsWatched(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Wheel spins',
                        initialValue: state.playerState.inventory.wheelSpins,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setWheelSpins(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'InitialLoginDaysAgo',
                        initialValue: Math.floor((DateHelper.now.getDay() - new Date(Game.store.getState().playerState.timers.get(TimerType.INITIAL_LOGIN)).getTime()) / DateHelper.Day),
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setInitialLogin(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Day of week',
                        initialValue: Math.floor(DateHelper.now.getDay() / DateHelper.Day),
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setDay(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Player purchases',
                        initialValue: state.playerState.purchases,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setPlayerPurchases(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Pet level',
                        initialValue: state.playerState.petLevel,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setPetLevel(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'DifficultyReduction',
                        initialValue: state.playerState.progress.difficultyReduction,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setDifficultyReduction(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'StarChestStartCount',
                        initialValue: state.playerState.progress.starChestInitialStars,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setStarChestStarCount(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Star chest level',
                        initialValue: state.playerState.starChestLevel || -1,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setStarChestLevel(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.TOGGLE,
                        propName: 'Cinematic mode',
                        initialValue: state.playerState.settings.cinematicMode,
                        onChange: value => {
                            cheats.cinematicMode(value);
                        }
                    },
                    {
                        type: PropType.TOGGLE,
                        propName: 'Pause harvest timer',
                        initialValue: Game.settings.harvestPaused || false,
                        onChange: value => {
                            cheats.pauseHarvestTimer(value);
                        }
                    },
                ],
            },
            {
                folderName: 'Boosters',
                props: [
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'TNT',
                        initialValue: state.playerState.inventory[BoosterType.TNT],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setBoosterTNT(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Bulls Eye',
                        initialValue: state.playerState.inventory[BoosterType.BULLS_EYE],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setBoosterBullsEye(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Shuffle',
                        initialValue: state.playerState.inventory[BoosterType.SHUFFLE],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setBoosterShuffle(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Undo',
                        initialValue: state.playerState.inventory[BoosterType.UNDO],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setBoosterUndo(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Joker',
                        initialValue: state.playerState.inventory[BoosterType.JOKER],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setBoosterJoker(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Booster Five',
                        initialValue: state.playerState.inventory[BoosterType.PLUS_FIVE],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setBoosterPlusFive(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Extra Cards',
                        initialValue: state.playerState.inventory[BoosterType.PLUS_ONE],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setExtraCards(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Canister Pre-Booster',
                        initialValue: state.playerState.inventory[PreBoosterType.CANISTER],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setPreBoosterCanister(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Joker Pre-Booster',
                        initialValue: state.playerState.inventory[PreBoosterType.JOKER],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setPreBoosterJoker(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Remove 3 Pre-Booster',
                        initialValue: state.playerState.inventory[PreBoosterType.REMOVE_THREE],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setPreBoosterRemoveThree(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Free Play',
                        initialValue: state.playerState.inventory[RewardType.FREE_PLAY],
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setFreePlay(value);
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'Shop Gift',
                        initialValue: state.playerState.inventory.shopGift,
                        buttonLabel: 'Set',
                        onButtonClick: value => {
                            cheats.setShopGift(value);
                        },
                        onFinishChange: _ => { },
                    },
                ],
            },
            {
                folderName: 'Missions',
                props: [
                    {
                        type: PropType.LIST_WITH_BUTTON,
                        propName: 'Select Mission',
                        initialValue: "1",
                        buttonLabel: 'Complete',
                        items: ["1", "2", "3"],
                        onButtonClick: (value, index): void => {
                            cheats.setMissionComplete(index);
                        },
                        onFinishChange: (_) => { },
                    },
                    {
                        folderName: 'Set Mission Progress',
                        props: [
                            {
                                type: PropType.LIST,
                                propName: 'Select Mission',
                                initialValue: (selectedMission + 1).toString(),
                                items: ["1", "2", "3"],
                                onChange: (value, index) => {
                                    selectedMission = index;
                                },
                            },
                            {
                                type: PropType.NUMBER_WITH_BUTTON,
                                propName: 'Set Progress',
                                initialValue: 0,
                                buttonLabel: 'Set',
                                onButtonClick: (value) => {
                                    cheats.setMissionProgress(selectedMission, value);
                                },
                                onFinishChange: (_) => { },
                            },
                        ],
                    },
                    {
                        folderName: 'Add Event Mission Progress',
                        props: [
                            {
                                type: PropType.NUMBER_WITH_BUTTON,
                                propName: 'Progress',
                                initialValue: 0,
                                buttonLabel: 'Add',
                                onButtonClick: (value) => {
                                    cheats.setQuestProgressByType('collectStars', value);
                                },
                                onFinishChange: (_) => { },
                            },
                        ],
                    },
                    {
                        folderName: 'Season Pass',
                        props: [
                            {
                                type: PropType.NUMBER_WITH_BUTTON,
                                propName: 'Add Points',
                                initialValue: 0,
                                buttonLabel: 'Add',
                                onButtonClick: value => {
                                    cheats.addSeasonPassPoints(value);
                                },
                                onFinishChange: _ => { },
                            },
                            {
                                type: PropType.BUTTON,
                                propName: 'Reset Season Pass',
                                onPress: () => {
                                    cheats.resetSeasonPass();
                                },
                            },
                        ],
                    },
                ],
            },
            {
                folderName: 'Logs',
                props: [
                    {
                        type: PropType.TOGGLE,
                        propName: 'Console',
                        initialValue: this.mobileConsole.visible,
                        onChange: value => {
                            this.mobileConsole.visible = value;
                        }
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Fetch FB Data',
                        onPress: async () => {
                            Logger.log('FB data', { origin: LogOrigin.CHEATS, data: [await cheats.fetchFBData()] });
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Fetch GB Data',
                        onPress: async () => {
                            Logger.log('GB data', { origin: LogOrigin.CHEATS, data: [await cheats.fetchGBCData()] });
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Show State',
                        onPress: async () => {
                            cheats.showState();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Show Player Data',
                        onPress: () => {
                            cheats.dumpPlayerData();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Marketing Campaign',
                        onPress: async () => {
                            Logger.log(cheats.getMarketingCampaign(), LogOrigin.CHEATS);
                        },
                    },
                ],
            },
            {
                folderName: 'No Params',
                props: [
                    {
                        type: PropType.BUTTON,
                        propName: 'Star Chest Fail',
                        onPress: () => {
                            cheats.playStarChestFallAnimation();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Star Chest FTUX',
                        onPress: () => {
                            cheats.playStarChestFTUX();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Clear Last Sunday Purchase',
                        onPress: () => {
                            cheats.clearLastSundayPurchase();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Clear Pet Data',
                        onPress: () => {
                            cheats.clearPetData();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Debug XPromo',
                        onPress: () => {
                            cheats.debugXPromo();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Fail Platform Ads',
                        onPress: () => {
                            cheats.setAdDebugMode(AdDebugMode.FAIL_PLATFORM_ONLY);
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset Ad Debug Mode',
                        onPress: () => {
                            cheats.setAdDebugMode(AdDebugMode.DEFAULT);
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'Reset Last Claimed Spin Level',
                        onPress: () => {
                            cheats.resetLastClaimedSpinLevel();
                        },
                    },
                ],
            },
            {
                folderName: 'With Params',
                props: [
                    {
                        type: PropType.BUTTON,
                        propName: 'Set cheats global',
                        onPress: () => {
                            // @ts-ignore
                            window.cheats = cheats;
                        },
                    },
                    {
                        folderName: 'locale',
                        props: [
                            {
                                type: PropType.LIST,
                                propName: 'language',
                                initialValue: 'en_US',
                                items: Object.values(LanguageKey),
                                onChange: (_value: LanguageKey) => {
                                    cheats.setLanguage(_value);
                                },
                            }
                        ],
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'unlockBackcard',
                        initialValue: 0,
                        buttonLabel: 'Set backcardId',
                        onButtonClick: value => {
                            cheats.unlockBackcard(value)
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'upgradeStarChest',
                        initialValue: 0,
                        buttonLabel: 'Set Tier',
                        onButtonClick: value => {
                            cheats.upgradeStarChest(value)
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'claimMissionReward',
                        initialValue: 0,
                        buttonLabel: 'Set Quest Index',
                        onButtonClick: value => {
                            cheats.claimMissionReward(value)
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'completeMissionTimer',
                        initialValue: 0,
                        buttonLabel: 'Set Quest Index',
                        onButtonClick: value => {
                            cheats.completeMissionTimer(value)
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.NUMBER_WITH_BUTTON,
                        propName: 'setLoginStreak',
                        initialValue: 0,
                        buttonLabel: 'Set Streak',
                        onButtonClick: value => {
                            cheats.setLoginStreak(value)
                        },
                        onFinishChange: _ => { },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'setQuestProgress',
                        onPress: () => {
                            this.mobileConsole.prompt('cheats.setQuestProgress(questNumber: number, progress: number)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'setBoosterReward',
                        onPress: () => {
                            this.mobileConsole.prompt('cheats.setBoosterReward(boosterType: BoosterType, value: number)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'setBoosterCoins',
                        onPress: () => {
                            Logger.log(JSON.stringify(PlayerTask, null, 2), LogOrigin.CHEATS);
                            this.mobileConsole.prompt('cheats.setBoosterCoins(type: PlayerTask, value: number)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'showPopup',
                        onPress: () => {
                            Logger.log(JSON.stringify(PopupID, null, 2), LogOrigin.CHEATS);
                            this.mobileConsole.prompt('cheats.showPopup(popupName: PopupID, params: any)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'playStarChestStarsRewardAnimation',
                        onPress: () => {
                            this.mobileConsole.prompt(
                                'cheats.playStarChestStarsRewardAnimation(amount: number, levelNumber?: number)'
                            );
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'setMissionProgress',
                        onPress: () => {
                            this.mobileConsole.prompt('cheats.setMissionProgress(questIndex: number, progress: number)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'setMissionType',
                        onPress: () => {
                            this.mobileConsole.prompt(
                                'cheats.setMissionType(questIndex: number, questType: QuestType, tier?: number)'
                            );
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'setActivePet',
                        onPress: () => {
                            Logger.log(JSON.stringify(PetId, null, 2), LogOrigin.CHEATS);
                            this.mobileConsole.prompt('cheats.setActivePet(value: PetId, reload: boolean = true)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'movePet',
                        onPress: () => {
                            this.mobileConsole.prompt('cheats.movePet(nodesToMove: number, reload: boolean)');
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'startPetMiniGame',
                        onPress: () => {
                            this.mobileConsole.prompt('cheats.startPetMiniGame(props: { forceJackpotWin?: boolean })');
                        },
                    },
                    {
                        type: PropType.STRING,
                        propName: 'setMarketingCampaign',
                        initialValue: '',
                        onFinishChange: value => {
                            cheats.setMarketingCampaign(value)
                        },
                    },
                    {
                        type: PropType.TOGGLE,
                        propName: 'showFriendHelper',
                        initialValue: false,
                        onChange: value => {
                            cheats.showFriendHelper(value);
                        }
                    },
                ],
            },
            {
                folderName: 'IAP',
                props: [
                    {
                        type: PropType.TOGGLE,
                        propName: 'toggleIAP',
                        initialValue: false,
                        onChange: value => {
                            cheats.toggleIAPs(value);
                        },
                    },
                    {
                        type: PropType.TOGGLE,
                        propName: 'toggleIAPFail',
                        initialValue: false,
                        onChange: value => {
                            cheats.toggleIAPFail(value);
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'getTotalUniversalValue',
                        onPress: () => {
                            console.log(cheats.getTotalPurchaseValue());
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'getHighestUniversalValue',
                        onPress: () => {
                            console.log(cheats.getHighestUniversalValue());
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'get Catalogue',
                        onPress: () => {
                            console.log(Game.sdk.getCatalog());
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'reset IAPs',
                        onPress: () => {
                            cheats.resetIAP();
                        },
                    },
                    {
                        type: PropType.BUTTON,
                        propName: 'disable IAP',
                        onPress: () => {
                            cheats.disableIAP();
                        },
                    },
                ],
            },
            {
                folderName: 'Simulator',
                props: [
                    {
                        folderName: 'Personas',
                        // @ts-expect-error
                        props: [
                            ...getSimulatorPersonas().map((persona) => ({
                                type: PropType.TOGGLE,
                                propName: persona,
                                initialValue: personaEnabled(persona),
                                onChange: (enabled: boolean): void => {
                                    cheats.toggleSimulatorPersona(persona, enabled);
                                },
                            })),
                        ],
                    },
                    {
                        type: PropType.SLIDER_WITH_BUTTON,
                        propName: 'Attempts',
                        min: 1,
                        max: 1000,
                        initialValue: getSimulationAttempts(),
                        step: 1,
                        buttonLabel: 'Set',
                        onButtonClick: (value): void => {
                            cheats.setSimulatorAttempts(value);
                        },
                        onFinishChange: (_): void => { },
                    },
                ],
            },
        ];
    }

    public updateDebugMenu(state: IGameState) {
        if (!this.debugMenu) return;

        Object.entries(this.debugMenu.updateHandlers).forEach(([key, handler]) => {
            if (handler) {
                const prop = this.debugMenu.getProp(key);
                prop.setValue(handler(state));
            }
        });
    }
}
